import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useReducedMotion } from "motion/react";

const SCROLL_THRESHOLD = 8;
const COMPACT_TOGGLE_COOLDOWN_MS = 100;

export function useScrollCompact() {
  const prefersReducedMotion = useReducedMotion();
  const location = useLocation();
  const [isCompact, setIsCompact] = useState(false);
  const lastYRef = useRef(0);
  const isCompactRef = useRef(false);
  const lastToggleAtRef = useRef(0);
  const lastIntentRef = useRef<"compact" | "expand" | null>(null);

  useEffect(() => {
    lastYRef.current = window.scrollY;
    isCompactRef.current = false;
    lastIntentRef.current = null;
    setIsCompact(false);
  }, [location.pathname]);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const setCompact = (next: boolean, intent: "compact" | "expand") => {
      if (next === isCompactRef.current) return;

      const now = Date.now();
      if (
        lastIntentRef.current !== null &&
        lastIntentRef.current !== intent &&
        now - lastToggleAtRef.current < COMPACT_TOGGLE_COOLDOWN_MS
      ) {
        return;
      }

      lastIntentRef.current = intent;
      lastToggleAtRef.current = now;
      isCompactRef.current = next;
      setIsCompact(next);
    };

    const onScroll = () => {
      const currentY = window.scrollY;

      if (currentY <= 0) {
        lastYRef.current = currentY;
        if (isCompactRef.current) {
          lastIntentRef.current = "expand";
          isCompactRef.current = false;
          setIsCompact(false);
        }
        return;
      }

      const delta = currentY - lastYRef.current;
      lastYRef.current = currentY;

      if (Math.abs(delta) < SCROLL_THRESHOLD) return;

      if (delta > SCROLL_THRESHOLD) {
        setCompact(true, "compact");
      } else if (delta < -SCROLL_THRESHOLD) {
        setCompact(false, "expand");
      }
    };

    lastYRef.current = window.scrollY;
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [prefersReducedMotion]);

  return prefersReducedMotion ? false : isCompact;
}
